/** biome-ignore-all lint/a11y/useSemanticElements: The WAI-ARIA APG date grid intentionally uses explicit grid roles. */ /** biome-ignore-all lint/a11y/useFocusableInteractive: Focus is managed on the buttons contained by grid cells, not structural rows and cells. */ 'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { mergeAriaIds } from '../../../lib/aria';
import { useControlledLatch } from '../../../lib/controlled-state';
import { cn } from '../../../lib/utils';
import { Popover, PopoverAnchor, PopoverContent } from '../../ui/popover';
import { specraFocusRing } from '../ensigns/focus-ring';
import { SpecraInput } from '../ensigns/input';
import { SpecraFormField } from './form-field';
const defaultLabels = {
    openCalendar: 'Open calendar',
    chooseDate: 'Choose date',
    calendarGrid: 'Calendar dates',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    today: 'Today',
    clear: 'Clear'
};
const toIsoDate = (date)=>{
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
const fromIsoDate = (value)=>{
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    const parsed = new Date(year, month, day);
    return parsed.getFullYear() === year && parsed.getMonth() === month && parsed.getDate() === day ? parsed : null;
};
const escapeRegex = (value)=>value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const normalizeLocaleText = (value)=>value.replace(/[\u200e\u200f\u061c]/g, '').replace(/[\u00a0\u202f]/g, ' ').trim();
const localizedDigitNormalizer = (locale)=>{
    const formatter = new Intl.NumberFormat(locale, {
        useGrouping: false
    });
    const digits = new Map(Array.from({
        length: 10
    }, (_, digit)=>[
            formatter.format(digit),
            String(digit)
        ]));
    return (value)=>Array.from(value, (character)=>digits.get(character) ?? character).join('');
};
const literalPattern = (value)=>Array.from(value.replace(/[\u200e\u200f\u061c]/g, '').replace(/[\u00a0\u202f]/g, ' ')).map((character)=>/\s/.test(character) ? '\\s*' : escapeRegex(character)).join('');
const createDisplayDateParser = (locale)=>{
    const normalizeDigits = localizedDigitNormalizer(locale);
    const formatter = new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
    const referenceDate = new Date(2006, 10, 22);
    const monthNames = Array.from({
        length: 12
    }, (_, month)=>{
        const monthPart = formatter.formatToParts(new Date(2006, month, 22)).find((part)=>part.type === 'month')?.value;
        return normalizeDigits(normalizeLocaleText(monthPart ?? String(month + 1)));
    });
    const parts = formatter.formatToParts(referenceDate);
    const pattern = parts.map((part)=>{
        if (part.type === 'day') return '(?<day>\\d{1,2})';
        if (part.type === 'year') return '(?<year>\\d{4})';
        if (part.type === 'month') {
            return `(?<month>${monthNames.map(escapeRegex).sort((left, right)=>right.length - left.length).join('|')})`;
        }
        return literalPattern(part.value);
    }).join('');
    const displayPattern = new RegExp(`^${pattern}$`, 'iu');
    return (value)=>{
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return fromIsoDate(value);
        const normalizedValue = normalizeDigits(normalizeLocaleText(value));
        const match = displayPattern.exec(normalizedValue);
        if (!match?.groups) return null;
        const year = Number(match.groups.year);
        const day = Number(match.groups.day);
        const month = monthNames.findIndex((monthName)=>monthName.toLocaleLowerCase(locale) === match.groups?.month.toLocaleLowerCase(locale));
        const parsed = new Date(year, month, day);
        return month >= 0 && parsed.getFullYear() === year && parsed.getMonth() === month && parsed.getDate() === day ? parsed : null;
    };
};
const displayDate = (value, formatter)=>{
    const parsed = fromIsoDate(value);
    return parsed ? formatter.format(parsed) : value;
};
const firstDayOfWeek = (locale)=>{
    try {
        const localeInfo = new Intl.Locale(locale);
        const firstDay = localeInfo.getWeekInfo?.().firstDay ?? localeInfo.weekInfo?.firstDay;
        return firstDay === 7 ? 0 : firstDay ?? 0;
    } catch  {
        return 0;
    }
};
const calendarDays = (month, weekStart)=>{
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const start = new Date(first);
    start.setDate(1 - (first.getDay() - weekStart + 7) % 7);
    return Array.from({
        length: 42
    }, (_, index)=>{
        const day = new Date(start);
        day.setDate(start.getDate() + index);
        return day;
    });
};
const shiftMonthClamped = (date, monthOffset)=>{
    const targetMonth = new Date(date.getFullYear(), date.getMonth() + monthOffset, 1);
    const lastDay = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0).getDate();
    return new Date(targetMonth.getFullYear(), targetMonth.getMonth(), Math.min(date.getDate(), lastDay));
};
let currentDateSnapshot = toIsoDate(new Date());
let currentDateTimeout;
const currentDateListeners = new Set();
// One shared external store avoids installing a midnight timer per mounted
// field. It has no renderer ownership and tears the timer down with its final
// subscriber, so separate React roots can safely reuse it.
const scheduleCurrentDateRefresh = ()=>{
    const now = new Date();
    const nextDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    currentDateTimeout = setTimeout(()=>{
        currentDateSnapshot = toIsoDate(new Date());
        for (const listener of currentDateListeners)listener();
        if (currentDateListeners.size > 0) scheduleCurrentDateRefresh();
        else currentDateTimeout = undefined;
    }, nextDay.getTime() - now.getTime() + 100);
};
const subscribeToCurrentDate = (listener)=>{
    if (currentDateListeners.size === 0) currentDateSnapshot = toIsoDate(new Date());
    currentDateListeners.add(listener);
    if (currentDateListeners.size === 1) scheduleCurrentDateRefresh();
    return ()=>{
        currentDateListeners.delete(listener);
        if (currentDateListeners.size === 0 && currentDateTimeout) {
            clearTimeout(currentDateTimeout);
            currentDateTimeout = undefined;
        }
    };
};
const getCurrentDateSnapshot = ()=>currentDateSnapshot;
const getServerCurrentDateSnapshot = ()=>toIsoDate(new Date());
const useCurrentDate = ()=>{
    const currentIsoDate = React.useSyncExternalStore(subscribeToCurrentDate, getCurrentDateSnapshot, getServerCurrentDateSnapshot);
    return fromIsoDate(currentIsoDate) ?? new Date();
};
/** Date field with the prototype's compact, accessible calendar and a native fallback. */ export const SpecraDateField = /*#__PURE__*/ React.forwardRef(({ className, clearable = true, defaultValue, disabled, error, formatDisplayValue, form, getDayTestId, hint, id, label, labels, locale = 'en-US', min, max, name, onChange, onInvalidValue, onValueChange, parseDisplayValue, portalContainer, invalidValueMessage = 'Enter a valid date within the available range.', isDateUnavailable, picker = 'calendar', placeholder = 'e.g. Mar 14, 2026', readOnly, required, showToday = true, submissionInputRef, value, ...props }, forwardedRef)=>{
    const generatedId = React.useId();
    const resolvedLabels = {
        ...defaultLabels,
        ...labels
    };
    const controlId = id ?? `specra-date-${generatedId}`;
    const calendarId = `${controlId}-calendar`;
    const hiddenRef = React.useRef(null);
    const nativeInputRef = React.useRef(null);
    const visibleInputRef = React.useRef(null);
    const calendarButtonRef = React.useRef(null);
    const displayValueDirtyRef = React.useRef(false);
    const invalidSubmissionSignaledRef = React.useRef(false);
    const lastCommittedValueRef = React.useRef(undefined);
    const calendarRef = React.useRef(null);
    const dayRefs = React.useRef(new Map());
    const valueControlled = useControlledLatch(value !== undefined);
    const controlledValue = typeof value === 'string' ? value : '';
    const initialValue = typeof defaultValue === 'string' ? defaultValue : '';
    const [internalValue, setInternalValue] = React.useState(initialValue);
    const currentValue = valueControlled ? controlledValue : internalValue;
    const selectedDate = fromIsoDate(currentValue);
    const currentDate = useCurrentDate();
    const localeTools = React.useMemo(()=>({
            display: new Intl.DateTimeFormat(locale, {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            }),
            fullDate: new Intl.DateTimeFormat(locale, {
                dateStyle: 'full'
            }),
            monthLong: new Intl.DateTimeFormat(locale, {
                month: 'long'
            }),
            monthShort: new Intl.DateTimeFormat(locale, {
                month: 'short'
            }),
            parse: createDisplayDateParser(locale),
            weekStart: firstDayOfWeek(locale),
            weekdayLong: new Intl.DateTimeFormat(locale, {
                weekday: 'long'
            }),
            weekdayNarrow: new Intl.DateTimeFormat(locale, {
                weekday: 'narrow'
            })
        }), [
        locale
    ]);
    const formatValue = React.useCallback((isoDate)=>formatDisplayValue?.(isoDate) ?? displayDate(isoDate, localeTools.display), [
        formatDisplayValue,
        localeTools.display
    ]);
    const parseValue = React.useCallback((nextDisplayValue)=>parseDisplayValue?.(nextDisplayValue) ?? localeTools.parse(nextDisplayValue), [
        localeTools.parse,
        parseDisplayValue
    ]);
    const [displayValue, setDisplayValue] = React.useState(()=>formatValue(currentValue));
    const formatValueRef = React.useRef(formatValue);
    formatValueRef.current = formatValue;
    const lastDisplayLocaleRef = React.useRef(locale);
    const [internalError, setInternalError] = React.useState();
    const [open, setOpen] = React.useState(false);
    const [pickerMode, setPickerMode] = React.useState('days');
    const [visibleMonth, setVisibleMonth] = React.useState(()=>selectedDate ?? new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
    const [focusedDate, setFocusedDate] = React.useState(()=>selectedDate ?? currentDate);
    const resolvedError = error ?? internalError;
    // SpecraFormField cannot clone control props through the Radix Popover root,
    // so the visible input mirrors its generated hint/error id contract here.
    const describedBy = mergeAriaIds(hint ? `${controlId}-hint` : undefined, resolvedError ? `${controlId}-error` : undefined);
    const minDate = React.useMemo(()=>min ? fromIsoDate(min) : null, [
        min
    ]);
    const maxDate = React.useMemo(()=>max ? fromIsoDate(max) : null, [
        max
    ]);
    const unavailable = React.useCallback((date)=>{
        const calendarDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        return Boolean(minDate && calendarDate < minDate || maxDate && calendarDate > maxDate || isDateUnavailable?.(calendarDate));
    }, [
        isDateUnavailable,
        maxDate,
        minDate
    ]);
    const weekStart = localeTools.weekStart;
    const days = React.useMemo(()=>calendarDays(visibleMonth, weekStart), [
        visibleMonth,
        weekStart
    ]);
    const dayAvailability = React.useMemo(()=>new Map(days.map((day)=>[
                toIsoDate(day),
                unavailable(day)
            ])), [
        days,
        unavailable
    ]);
    React.useImperativeHandle(forwardedRef, ()=>picker === 'native' ? nativeInputRef.current : visibleInputRef.current, [
        picker
    ]);
    React.useImperativeHandle(submissionInputRef, ()=>picker === 'native' ? nativeInputRef.current : hiddenRef.current, [
        picker
    ]);
    React.useEffect(()=>{
        const localeChanged = lastDisplayLocaleRef.current !== locale;
        lastDisplayLocaleRef.current = locale;
        const valueWasCommittedWhileEditing = displayValueDirtyRef.current && lastCommittedValueRef.current === currentValue;
        lastCommittedValueRef.current = undefined;
        if (valueWasCommittedWhileEditing && !localeChanged) return;
        setDisplayValue(formatValueRef.current(currentValue));
        visibleInputRef.current?.setCustomValidity('');
        setInternalError(undefined);
        displayValueDirtyRef.current = false;
        invalidSubmissionSignaledRef.current = false;
    }, [
        currentValue,
        locale
    ]);
    React.useEffect(()=>{
        const input = hiddenRef.current;
        if (!input || input.value === currentValue) return;
        const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
        setter?.call(input, currentValue);
    }, [
        currentValue
    ]);
    React.useEffect(()=>{
        if (!open) setPickerMode('days');
    }, [
        open
    ]);
    React.useEffect(()=>{
        if (!open) return undefined;
        // The calendar is portalled and may itself live inside a portalled dialog.
        // These capture listeners keep the compound field's dismissal atomic: Escape
        // closes the calendar before an ancestor overlay, while focus/pointer exits
        // still synchronize the controlled Radix popover state.
        const handlePointerDown = (event)=>{
            const target = event.target;
            if (!calendarRef.current?.contains(target) && !visibleInputRef.current?.parentElement?.contains(target)) {
                setOpen(false);
            }
        };
        const handleFocusIn = (event)=>{
            const target = event.target;
            if (!calendarRef.current?.contains(target) && !visibleInputRef.current?.parentElement?.contains(target)) {
                setOpen(false);
            }
        };
        const handleEscapeCapture = (event)=>{
            if (event.key !== 'Escape') return;
            const target = event.target;
            if (!(target instanceof Node)) return;
            const calendarHasTarget = Boolean(calendarRef.current?.contains(target));
            const compoundFieldHasTarget = Boolean(visibleInputRef.current?.parentElement?.contains(target));
            if (!calendarHasTarget && !compoundFieldHasTarget) return;
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
            if (calendarHasTarget) {
                requestAnimationFrame(()=>calendarButtonRef.current?.focus());
            }
        };
        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('focusin', handleFocusIn);
        document.addEventListener('keydown', handleEscapeCapture, true);
        return ()=>{
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('focusin', handleFocusIn);
            document.removeEventListener('keydown', handleEscapeCapture, true);
        };
    }, [
        open
    ]);
    const commitValue = (nextValue)=>{
        lastCommittedValueRef.current = nextValue;
        visibleInputRef.current?.setCustomValidity('');
        if (!valueControlled) setInternalValue(nextValue);
        onValueChange?.(nextValue);
        const input = hiddenRef.current;
        if (input && input.value !== nextValue) {
            const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
            setter?.call(input, nextValue);
            input.dispatchEvent(new Event('input', {
                bubbles: true
            }));
        }
    };
    const commitParsedValue = (rawValue)=>{
        if (!rawValue.trim()) {
            setInternalError(undefined);
            commitValue('');
            return true;
        }
        const parsed = parseValue(rawValue);
        if (!parsed || unavailable(parsed)) {
            visibleInputRef.current?.setCustomValidity(typeof invalidValueMessage === 'string' ? invalidValueMessage : 'Enter a valid date.');
            setInternalError(invalidValueMessage);
            onInvalidValue?.(rawValue);
            return false;
        }
        setInternalError(undefined);
        const nextValue = toIsoDate(parsed);
        commitValue(nextValue);
        setDisplayValue(formatValue(nextValue));
        return true;
    };
    const focusDate = (date, direction = 1)=>{
        let nextDate = minDate && date < minDate ? minDate : maxDate && date > maxDate ? maxDate : date;
        for(let attempts = 0; attempts < 366 && unavailable(nextDate); attempts += 1){
            const candidate = new Date(nextDate);
            candidate.setDate(nextDate.getDate() + direction);
            if (minDate && candidate < minDate || maxDate && candidate > maxDate) return undefined;
            nextDate = candidate;
        }
        if (unavailable(nextDate)) return undefined;
        const iso = toIsoDate(nextDate);
        const remainsVisible = nextDate.getFullYear() === visibleMonth.getFullYear() && nextDate.getMonth() === visibleMonth.getMonth();
        setFocusedDate(nextDate);
        if (!remainsVisible) {
            setVisibleMonth(new Date(nextDate.getFullYear(), nextDate.getMonth(), 1));
        }
        if (remainsVisible) {
            const day = dayRefs.current.get(iso);
            if (day) day.focus();
            else requestAnimationFrame(()=>dayRefs.current.get(iso)?.focus());
        } else {
            requestAnimationFrame(()=>dayRefs.current.get(iso)?.focus());
        }
        return iso;
    };
    const toggleCalendarFromButton = ()=>{
        if (open) {
            setOpen(false);
            return;
        }
        const focusedIsoDate = focusDate(selectedDate ?? focusedDate);
        setOpen(true);
        requestAnimationFrame(()=>{
            if (focusedIsoDate) dayRefs.current.get(focusedIsoDate)?.focus();
        });
    };
    const navigateVisibleMonth = (direction)=>{
        const nextMonth = pickerMode === 'years' ? new Date(visibleMonth.getFullYear() + direction * 12, visibleMonth.getMonth(), 1) : pickerMode === 'months' ? new Date(visibleMonth.getFullYear() + direction, visibleMonth.getMonth(), 1) : new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + direction, 1);
        setVisibleMonth(nextMonth);
        setFocusedDate(nextMonth);
    };
    const closeCalendarAndReturnFocus = ()=>{
        setOpen(false);
        requestAnimationFrame(()=>calendarButtonRef.current?.focus());
    };
    if (picker === 'native') {
        return /*#__PURE__*/ _jsx(SpecraFormField, {
            className: className,
            error: resolvedError,
            hint: hint,
            htmlFor: controlId,
            label: label,
            required: required,
            children: /*#__PURE__*/ _jsx(SpecraInput, {
                id: controlId,
                invalid: Boolean(resolvedError),
                ref: nativeInputRef,
                required: required,
                type: "date",
                ...props,
                defaultValue: valueControlled ? undefined : defaultValue,
                disabled: disabled,
                max: max,
                min: min,
                name: name,
                onChange: (event)=>{
                    onChange?.(event);
                    onValueChange?.(event.currentTarget.value);
                },
                readOnly: readOnly,
                form: form,
                value: valueControlled ? controlledValue : undefined
            })
        });
    }
    const monthLabel = localeTools.monthLong.format(visibleMonth);
    const dayLabels = Array.from({
        length: 7
    }, (_, index)=>{
        const date = new Date(2026, 0, 4 + (weekStart + index) % 7);
        return {
            id: String(index),
            label: localeTools.weekdayNarrow.format(date),
            accessibleLabel: localeTools.weekdayLong.format(date)
        };
    });
    const monthOptions = Array.from({
        length: 12
    }, (_, month)=>({
            label: localeTools.monthShort.format(new Date(2026, month, 1)),
            value: month
        }));
    const today = toIsoDate(currentDate);
    const focusedIso = toIsoDate(focusedDate);
    const rovingDay = days.find((day)=>toIsoDate(day) === focusedIso && !dayAvailability.get(toIsoDate(day))) ?? days.find((day)=>day.getMonth() === visibleMonth.getMonth() && !dayAvailability.get(toIsoDate(day))) ?? days.find((day)=>!dayAvailability.get(toIsoDate(day))) ?? days.find((day)=>day.getMonth() === visibleMonth.getMonth()) ?? days[0];
    const rovingIso = rovingDay ? toIsoDate(rovingDay) : undefined;
    return /*#__PURE__*/ _jsx(SpecraFormField, {
        className: className,
        error: resolvedError,
        hint: hint,
        htmlFor: controlId,
        label: label,
        required: required,
        children: /*#__PURE__*/ _jsxs(Popover, {
            onOpenChange: setOpen,
            open: open,
            children: [
                /*#__PURE__*/ _jsx(PopoverAnchor, {
                    asChild: true,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "relative w-full",
                        children: [
                            /*#__PURE__*/ _jsx("input", {
                                "aria-hidden": "true",
                                className: "pointer-events-none absolute size-px opacity-0",
                                disabled: disabled,
                                name: name,
                                onChange: (event)=>onChange?.(event),
                                readOnly: true,
                                ref: hiddenRef,
                                tabIndex: -1,
                                type: "text",
                                defaultValue: currentValue,
                                form: form
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                ...props,
                                "aria-controls": open ? calendarId : undefined,
                                "aria-describedby": mergeAriaIds(props['aria-describedby'], describedBy),
                                "aria-expanded": open,
                                "aria-haspopup": "dialog",
                                autoComplete: "off",
                                disabled: disabled,
                                id: controlId,
                                invalid: Boolean(resolvedError),
                                onChange: (event)=>{
                                    const nextDisplay = event.target.value;
                                    displayValueDirtyRef.current = true;
                                    setDisplayValue(nextDisplay);
                                    const parsed = parseValue(nextDisplay);
                                    const validDate = parsed && !unavailable(parsed);
                                    if (validDate) {
                                        invalidSubmissionSignaledRef.current = false;
                                        setInternalError(undefined);
                                        commitValue(toIsoDate(parsed));
                                    } else if (currentValue && !invalidSubmissionSignaledRef.current) {
                                        // Clear the committed value immediately while preserving the user's draft text.
                                        // This keeps controlled host state and native form submission in agreement.
                                        invalidSubmissionSignaledRef.current = true;
                                        commitValue('');
                                    }
                                    event.currentTarget.setCustomValidity(nextDisplay.trim() && !validDate ? typeof invalidValueMessage === 'string' ? invalidValueMessage : 'Enter a valid date.' : '');
                                },
                                onBlur: (event)=>{
                                    const nextFocus = event.relatedTarget;
                                    const selectingCalendarDay = nextFocus instanceof Element && Boolean(nextFocus.closest('[data-date]'));
                                    if (displayValueDirtyRef.current && !selectingCalendarDay) {
                                        commitParsedValue(event.currentTarget.value);
                                        displayValueDirtyRef.current = false;
                                    }
                                    props.onBlur?.(event);
                                },
                                onFocus: props.onFocus,
                                onKeyDown: (event)=>{
                                    if (event.key === 'ArrowDown') {
                                        event.preventDefault();
                                        setOpen(true);
                                        focusDate(selectedDate ?? focusedDate);
                                    }
                                    props.onKeyDown?.(event);
                                },
                                placeholder: placeholder,
                                readOnly: readOnly,
                                required: required,
                                value: displayValue,
                                form: form,
                                ref: visibleInputRef
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                "aria-label": resolvedLabels.openCalendar,
                                "aria-controls": open ? calendarId : undefined,
                                "aria-expanded": open,
                                "aria-haspopup": "dialog",
                                className: cn('absolute inset-y-0 right-[var(--space-1)] my-auto flex size-[var(--control-height-sm)] items-center justify-center rounded-sm text-[color:var(--text-faint)] hover:bg-[color:var(--surface-nav-hover)] hover:text-foreground', specraFocusRing),
                                disabled: disabled || readOnly,
                                onClick: toggleCalendarFromButton,
                                ref: calendarButtonRef,
                                type: "button",
                                children: /*#__PURE__*/ _jsx(Calendar, {
                                    "aria-hidden": "true",
                                    className: "size-3.5"
                                })
                            })
                        ]
                    })
                }),
                open ? /*#__PURE__*/ _jsxs(PopoverContent, {
                    align: "start",
                    "aria-label": resolvedLabels.chooseDate,
                    className: "z-50 w-[var(--radix-popover-anchor-width)] min-w-64 max-w-[calc(100vw-var(--component-overlay-edge-gap))] rounded-md border border-[color:var(--border-card)] bg-[color:var(--surface-panel)] px-[var(--space-3)] pb-[var(--space-3)] pt-[var(--space-2-5)] shadow-md",
                    id: calendarId,
                    onInteractOutside: (event)=>{
                        const target = event.target;
                        if (target instanceof Node && visibleInputRef.current?.parentElement?.contains(target)) {
                            event.preventDefault();
                        }
                    },
                    portalContainer: portalContainer,
                    ref: calendarRef,
                    role: "dialog",
                    sideOffset: 6,
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-[var(--space-1)]",
                            children: [
                                /*#__PURE__*/ _jsx("button", {
                                    "aria-label": resolvedLabels.previousMonth,
                                    className: cn('flex rounded-sm p-[var(--space-row-y)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface-nav-hover)] hover:text-foreground', specraFocusRing),
                                    onClick: ()=>navigateVisibleMonth(-1),
                                    type: "button",
                                    children: /*#__PURE__*/ _jsx(ChevronLeft, {
                                        "aria-hidden": "true",
                                        className: "size-3.5"
                                    })
                                }),
                                /*#__PURE__*/ _jsx("button", {
                                    "aria-expanded": pickerMode === 'months',
                                    className: cn('flex-1 rounded-sm py-[var(--space-row-y)] text-center text-[length:var(--fs-ui-caption)] font-medium hover:bg-[color:var(--surface-nav-hover)]', specraFocusRing),
                                    onClick: ()=>setPickerMode((mode)=>mode === 'months' ? 'days' : 'months'),
                                    type: "button",
                                    children: monthLabel
                                }),
                                /*#__PURE__*/ _jsx("button", {
                                    "aria-expanded": pickerMode === 'years',
                                    className: cn('rounded-sm px-[var(--space-2)] py-[var(--space-row-y)] text-center text-[length:var(--fs-ui-caption)] font-medium tabular-nums hover:bg-[color:var(--surface-nav-hover)]', specraFocusRing),
                                    onClick: ()=>setPickerMode((mode)=>mode === 'years' ? 'days' : 'years'),
                                    type: "button",
                                    children: visibleMonth.getFullYear()
                                }),
                                /*#__PURE__*/ _jsx("button", {
                                    "aria-label": resolvedLabels.nextMonth,
                                    className: cn('flex rounded-sm p-[var(--space-row-y)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface-nav-hover)] hover:text-foreground', specraFocusRing),
                                    onClick: ()=>navigateVisibleMonth(1),
                                    type: "button",
                                    children: /*#__PURE__*/ _jsx(ChevronRight, {
                                        "aria-hidden": "true",
                                        className: "size-3.5"
                                    })
                                })
                            ]
                        }),
                        pickerMode === 'months' ? /*#__PURE__*/ _jsx("div", {
                            className: "mt-[var(--space-2)] grid grid-cols-4 gap-[var(--space-1)]",
                            children: monthOptions.map((month)=>/*#__PURE__*/ _jsx("button", {
                                    "aria-pressed": month.value === visibleMonth.getMonth(),
                                    className: cn('rounded-sm py-[var(--space-2)] text-[length:var(--fs-ui-meta)] hover:bg-[color:var(--surface-nav-hover)]', month.value === visibleMonth.getMonth() && 'bg-[color:var(--surface-chip)] font-medium'),
                                    onClick: ()=>{
                                        const nextMonth = new Date(visibleMonth.getFullYear(), month.value, 1);
                                        setVisibleMonth(nextMonth);
                                        setFocusedDate(nextMonth);
                                        setPickerMode('days');
                                    },
                                    type: "button",
                                    children: month.label
                                }, month.value))
                        }) : pickerMode === 'years' ? /*#__PURE__*/ _jsx("div", {
                            className: "mt-[var(--space-2)] grid grid-cols-4 gap-[var(--space-1)]",
                            children: Array.from({
                                length: 12
                            }, (_, index)=>visibleMonth.getFullYear() - 5 + index).map((year)=>/*#__PURE__*/ _jsx("button", {
                                    "aria-pressed": year === visibleMonth.getFullYear(),
                                    className: cn('rounded-sm py-[var(--space-2)] text-[length:var(--fs-ui-meta)] tabular-nums hover:bg-[color:var(--surface-nav-hover)]', year === visibleMonth.getFullYear() && 'bg-[color:var(--surface-chip)] font-medium'),
                                    onClick: ()=>{
                                        const nextMonth = new Date(year, visibleMonth.getMonth(), 1);
                                        setVisibleMonth(nextMonth);
                                        setFocusedDate(nextMonth);
                                        setPickerMode('days');
                                    },
                                    type: "button",
                                    children: year
                                }, year))
                        }) : /*#__PURE__*/ _jsxs("div", {
                            "aria-label": resolvedLabels.calendarGrid,
                            className: "mt-[var(--space-2)] space-y-[var(--space-0-5)]",
                            role: "grid",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "grid grid-cols-7 gap-[var(--space-0-5)]",
                                    role: "row",
                                    children: dayLabels.map((day)=>/*#__PURE__*/ _jsx("div", {
                                            "aria-label": day.accessibleLabel,
                                            className: "py-[var(--space-0-5)] text-center text-[length:var(--fs-ui-micro)] font-medium uppercase tracking-[var(--ls-ui-label)] text-[color:var(--text-faint)]",
                                            role: "columnheader",
                                            children: day.label
                                        }, day.id))
                                }),
                                Array.from({
                                    length: 6
                                }, (_, weekIndex)=>/*#__PURE__*/ _jsx("div", {
                                        className: "grid grid-cols-7 gap-[var(--space-0-5)]",
                                        role: "row",
                                        children: days.slice(weekIndex * 7, weekIndex * 7 + 7).map((day)=>{
                                            const iso = toIsoDate(day);
                                            const inMonth = day.getMonth() === visibleMonth.getMonth();
                                            const selected = iso === currentValue;
                                            const dayUnavailable = dayAvailability.get(iso) ?? false;
                                            const focused = iso === rovingIso;
                                            return /*#__PURE__*/ _jsx("div", {
                                                "aria-selected": selected || undefined,
                                                className: "min-w-0",
                                                role: "gridcell",
                                                children: /*#__PURE__*/ _jsx("button", {
                                                    "aria-current": iso === today ? 'date' : undefined,
                                                    "aria-label": localeTools.fullDate.format(day),
                                                    className: cn('h-[var(--component-calendar-day-height)] w-full rounded-sm border border-transparent text-[length:var(--fs-ui-meta)] tabular-nums transition-colors aria-disabled:cursor-not-allowed aria-disabled:opacity-40', inMonth ? 'text-foreground hover:bg-[color:var(--surface-nav-hover)]' : 'text-[color:var(--text-ghost)]', iso === today && 'border-[color:var(--border-control)]', selected && 'border-[color:var(--cta-bg)] bg-[color:var(--cta-bg)] text-[color:var(--cta-text)]'),
                                                    "data-date": iso,
                                                    "data-testid": getDayTestId?.(iso),
                                                    "aria-disabled": dayUnavailable || undefined,
                                                    onClick: ()=>{
                                                        if (dayUnavailable) return;
                                                        displayValueDirtyRef.current = false;
                                                        commitValue(iso);
                                                        setInternalError(undefined);
                                                        setFocusedDate(day);
                                                        setVisibleMonth(new Date(day.getFullYear(), day.getMonth(), 1));
                                                        closeCalendarAndReturnFocus();
                                                    },
                                                    onFocus: ()=>setFocusedDate(day),
                                                    onKeyDown: (event)=>{
                                                        const dayIndex = (day.getDay() - weekStart + 7) % 7;
                                                        const offsets = {
                                                            ArrowDown: 7,
                                                            ArrowLeft: -1,
                                                            ArrowRight: 1,
                                                            ArrowUp: -7,
                                                            End: 6 - dayIndex,
                                                            Home: -dayIndex
                                                        };
                                                        if (event.key === 'PageDown' || event.key === 'PageUp') {
                                                            event.preventDefault();
                                                            focusDate(shiftMonthClamped(day, event.key === 'PageDown' ? 1 : -1), event.key === 'PageDown' ? 1 : -1);
                                                            return;
                                                        }
                                                        const offset = offsets[event.key];
                                                        if (offset !== undefined) {
                                                            event.preventDefault();
                                                            const nextDate = new Date(day);
                                                            nextDate.setDate(day.getDate() + offset);
                                                            focusDate(nextDate, offset < 0 ? -1 : 1);
                                                        }
                                                    },
                                                    ref: (node)=>{
                                                        if (node) dayRefs.current.set(iso, node);
                                                        else dayRefs.current.delete(iso);
                                                    },
                                                    tabIndex: focused ? 0 : -1,
                                                    type: "button",
                                                    children: day.getDate()
                                                })
                                            }, iso);
                                        })
                                    }, toIsoDate(days[weekIndex * 7])))
                            ]
                        }),
                        showToday || clearable ? /*#__PURE__*/ _jsxs("div", {
                            className: "mt-[var(--space-2)] flex border-t border-[color:var(--border-subtle)] pt-[var(--space-2)]",
                            children: [
                                showToday ? /*#__PURE__*/ _jsx("button", {
                                    className: cn('rounded-sm px-[var(--space-2)] py-[var(--space-row-y)] text-[length:var(--fs-ui-meta)] font-medium text-[color:var(--text-secondary)] hover:bg-[color:var(--surface-nav-hover)] hover:text-foreground', specraFocusRing),
                                    onClick: ()=>{
                                        const next = currentDate;
                                        if (!unavailable(next)) {
                                            commitValue(toIsoDate(next));
                                            setInternalError(undefined);
                                            setVisibleMonth(new Date(next.getFullYear(), next.getMonth(), 1));
                                            closeCalendarAndReturnFocus();
                                        }
                                    },
                                    disabled: unavailable(currentDate),
                                    type: "button",
                                    children: resolvedLabels.today
                                }) : null,
                                clearable ? /*#__PURE__*/ _jsx("button", {
                                    className: cn('ml-auto rounded-sm px-[var(--space-2)] py-[var(--space-row-y)] text-[length:var(--fs-ui-meta)] text-[color:var(--text-faint)] hover:bg-[color:var(--surface-nav-hover)] hover:text-foreground', specraFocusRing),
                                    onClick: ()=>{
                                        commitValue('');
                                        setInternalError(undefined);
                                        closeCalendarAndReturnFocus();
                                    },
                                    type: "button",
                                    children: resolvedLabels.clear
                                }) : null
                            ]
                        }) : null
                    ]
                }) : null
            ]
        })
    });
});
SpecraDateField.displayName = 'SpecraDateField';
